import { useState, useRef, useEffect } from "react";
import { formatCurrencyInput, formatPercentInput } from "@rebuild/shared";

type DisplayElement = "span" | "div" | "h1" | "h2" | "p";
type InputType = "text" | "textarea" | "currency" | "percent";

interface EditableTextProps {
  value: string;
  onChange: (value: string) => void;
  displayAs?: DisplayElement;
  inputType?: InputType;
  displayClassName?: string;
  editClassName?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
}

export function EditableText({
  value,
  onChange,
  displayAs = "span",
  inputType = "text",
  displayClassName = "",
  editClassName = "",
  prefix,
  suffix,
  disabled = false,
  placeholder,
}: EditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Move cursor to end for text inputs
      if (inputType !== "textarea") {
        const length = inputRef.current.value.length;
        inputRef.current.setSelectionRange(length, length);
      }
    }
  }, [isEditing, inputType]);

  const handleStartEdit = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const finishEditing = () => {
    if (inputType === "percent" && value.trim() === "") {
      onChange("0");
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      finishEditing();
    }
    // For non-textarea inputs, Enter also stops editing
    if (e.key === "Enter" && inputType !== "textarea") {
      finishEditing();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (inputType === "currency") {
      onChange(formatCurrencyInput(e.target.value));
    } else if (inputType === "percent") {
      onChange(formatPercentInput(e.target.value));
    } else {
      onChange(e.target.value);
    }
  };

  // Display mode
  if (!isEditing) {
    const DisplayTag = displayAs;
    const baseDisplayClassName = `cursor-pointer hover:bg-gray-100 rounded px-1 -mx-1 ${displayClassName}`;
    const displayValue = inputType === "currency" ? formatCurrencyInput(value || "0.00") : value;

    return (
      <DisplayTag className={disabled ? displayClassName : baseDisplayClassName} onClick={disabled ? undefined : handleStartEdit}>
        {prefix}
        {displayValue || placeholder}
        {suffix}
      </DisplayTag>
    );
  }

  // Edit mode
  const baseEditClassName = "border border-blue-500 rounded px-1 outline-none";

  if (inputType === "textarea") {
    return (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={value}
        onChange={handleChange}
        onBlur={finishEditing}
        onKeyDown={handleKeyDown}
        className={`${baseEditClassName} ${editClassName}`}
        placeholder={placeholder}
      />
    );
  }

  // For currency/percent, we wrap with prefix/suffix display
  if (inputType === "currency" || inputType === "percent") {
    return (
      <span className="flex items-center">
        {prefix}
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={value}
          onChange={handleChange}
          onBlur={finishEditing}
          onKeyDown={handleKeyDown}
          className={`${baseEditClassName} ${editClassName}`}
          placeholder={placeholder}
        />
        {suffix}
      </span>
    );
  }

  // Regular text input
  return (
    <input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      type="text"
      value={value}
      onChange={handleChange}
      onBlur={finishEditing}
      onKeyDown={handleKeyDown}
      className={`${baseEditClassName} ${editClassName}`}
      placeholder={placeholder}
    />
  );
}
